/**
 * In-memory copy of the last successful GET /api/v1/auth/context payload (user + branding + permissions).
 * Job position is also kept in sessionStorage so reports can read it after login.
 * Refresh via {@link ../api/sessionContext.fetchSessionContext}.
 */

const JOB_POSITION_CACHE_KEY = 'dth_login_job_positions';

let profile = null;

function readJobPositionCache() {
  if (typeof window === 'undefined' || !window.sessionStorage) return {};
  try {
    const raw = window.sessionStorage.getItem(JOB_POSITION_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeJobPositionCache(map) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(JOB_POSITION_CACHE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private-mode failures
  }
}

export function profileJobPosition(source) {
  if (!source) return '';
  const nested = source.user && typeof source.user === 'object' ? source.user : null;
  return String(
    source.jobPosition
    || source.job_position
    || nested?.jobPosition
    || nested?.job_position
    || ''
  ).trim();
}

export function cacheLoginJobPosition(source) {
  const job = profileJobPosition(source);
  if (!job) return;
  const id = source?.id != null ? String(source.id) : source?.user?.id != null ? String(source.user.id) : '';
  const email = String(source?.email || source?.user?.email || '')
    .trim()
    .toLowerCase();
  const name = String(source?.name || source?.user?.name || '')
    .trim()
    .toLowerCase();
  const map = readJobPositionCache();
  if (id) map[`id:${id}`] = job;
  if (email) map[`email:${email}`] = job;
  if (name) map[`name:${name}`] = job;
  writeJobPositionCache(map);
}

export function getCachedJobPosition({ id, email, name } = {}) {
  const map = readJobPositionCache();
  const emailKey = email ? `email:${String(email).trim().toLowerCase()}` : '';
  const idKey = id != null && String(id).trim() ? `id:${String(id).trim()}` : '';
  const nameKey = name ? `name:${String(name).trim().toLowerCase()}` : '';
  return String(
    (idKey && map[idKey])
    || (emailKey && map[emailKey])
    || (nameKey && map[nameKey])
    || ''
  ).trim();
}

export function setSessionProfile(next) {
  profile = next ?? null;
  cacheLoginJobPosition(next);
}

export function getSessionProfile() {
  return profile;
}

export function clearSessionProfile() {
  profile = null;
}

/** Registry tenant UUID for X-TenantID (from loaded profile). */
export function getRegistryTenantIdFromSession() {
  const tid = profile?.tenantId;
  if (tid == null) return null;
  const s = String(tid).trim();
  return s !== '' ? s : null;
}

export function getUserIdFromSession() {
  const id = profile?.id;
  return id != null ? id : null;
}
